import { useCallback } from 'react';
import { useSyncExternalStore } from 'react';
import { computeDayStats } from './weekStats';
import { dateKey, getDoneSets, subscribe } from './setProgress';
import { getDayOfWeek } from './scheduler';
import { getPlanForDay } from '../data/plan';

const MAX_LOOKBACK_DAYS = 365;

/**
 * Aufeinanderfolgende Tage mit ≥1 abgeschlossener Trainings-Uebung — von today rueckwaerts.
 * Pilates- und Recovery-Tage zaehlen als „erledigt" (Plan-konform, kein Set-Tracking noetig).
 * Wenn heute ein Trainingstag ist und noch 0 done sind, wird heute uebersprungen
 * (sonst waere die Streak vor dem Workout immer 0).
 */
export function computeStreak(today: Date): number {
  const date = new Date(today);
  date.setHours(0, 0, 0, 0);

  const todayStats = computeDayStats(date, getDoneSets);
  if (todayStats.plan.kind === 'training' && todayStats.completedExercises === 0) {
    date.setDate(date.getDate() - 1);
  }

  let streak = 0;
  for (let i = 0; i < MAX_LOOKBACK_DAYS; i += 1) {
    const stats = computeDayStats(date, getDoneSets);
    if (stats.plan.kind === 'training') {
      if (stats.completedExercises === 0) break;
      streak += 1;
    } else {
      // Pilates / Recovery zaehlen mit
      streak += 1;
    }
    date.setDate(date.getDate() - 1);
  }
  return streak;
}

/** Fingerprint, dessen String-Identitaet sich aendert, wann immer die Streak sich aendern koennte. */
function streakFingerprint(today: Date): string {
  const date = new Date(today);
  date.setHours(0, 0, 0, 0);
  const parts: string[] = [];
  // Lookback bis ein Trainingstag mit 0 done oder 30 Tage erreicht — fingerprint bleibt klein.
  for (let i = 0; i < 30; i += 1) {
    const k = dateKey(date);
    const plan = getPlanForDay(getDayOfWeek(date));
    if (plan.kind !== 'training') {
      parts.push(`${k}:${plan.kind}`);
    } else {
      const sum = plan.entries.reduce((acc, e) => acc + getDoneSets(k, e.exerciseId).length, 0);
      parts.push(`${k}:${sum}`);
    }
    date.setDate(date.getDate() - 1);
  }
  return parts.join('|');
}

export function useStreak(today: Date): number {
  const sub = useCallback((cb: () => void) => subscribe(cb), []);
  const getSnap = useCallback(() => streakFingerprint(today), [today]);
  useSyncExternalStore(sub, getSnap, getSnap);
  return computeStreak(today);
}
